<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { toRaw } from 'vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { checkMissingAttributes } = useConversationRequiredAttributes();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);
const resolveAttributesModalRef = ref(null);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

const currentChat = computed(() => getters.getSelectedChat.value);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

const toggleStatus = (status, snoozedUntil, customAttributes = null) => {
  closeDropdown();
  isLoading.value = true;

  const payload = {
    conversationId: currentChat.value.id,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
    isLoading.value = false;
  });
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (context) {
    const currentCustomAttributes = currentChat.value.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };
    toggleStatus(
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = () => {
  const currentCustomAttributes = currentChat.value.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    const conversationContext = {
      id: currentChat.value.id,
      snoozedUntil: null,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {

    const rawChat = toRaw(currentChat.value);
    console.log('rawchat', rawChat);
    if (rawChat.inbox_id === 1) {

      const requiredFields = [
        {
          name: 'Tipificación',
          field: 'tipificaciones'
        },
        {
          name: 'Observación',
          field: 'observaciones'
        }
      ];

      const customAttributes = currentChat.value.custom_attributes || {};

      // buscar los que faltan o están vacíos
      const missingFields = requiredFields.filter(
        f => !customAttributes[f.field] || String(customAttributes[f.field]).trim() === ''
      );

      if (missingFields.length > 0) {
        const missingNames = missingFields.map(f => f.name).join(', ');
        useAlert(
          t(`No puedes resolver la conversación sin diligenciar los siguientes campos: ${missingNames}`)
        );
        return;
      }

      const ayudaAdicional = currentChat.value.custom_attributes?.puedo_ayudarte_en_algo_adicional;
      const solicitudAdicional = currentChat.value.custom_attributes?.solicitud_adicional;

      if (ayudaAdicional === 'Si' && !solicitudAdicional) {
        useAlert(
          t(`No puedes resolver la conversación sin diligenciar el campo Solicitud adicional`)
        );
        return;
      }
    } else if (rawChat.inbox_id === 3) {

      const requiredFields = [
        {
          name: 'Segmento',
          field: 'segmento'
        },
        {
          name: 'Tipificación',
          field: 'tipificacion'
        },
        {
          name: 'Observación',
          field: 'observacion'
        }
      ];

      const customAttributes = currentChat.value.custom_attributes || {};

      // buscar los que faltan o están vacíos
      const missingFields = requiredFields.filter(
        f => !customAttributes[f.field] || String(customAttributes[f.field]).trim() === ''
      );

      if (missingFields.length > 0) {
        const missingNames = missingFields.map(f => f.name).join(', ');
        useAlert(
          t(`No puedes resolver la conversación sin diligenciar los siguientes campos: ${missingNames}`)
        );
        return;
      }

      const ayudaAdicional = currentChat.value.custom_attributes?.puedo_ayudarte_en_algo_adicional;
      const solicitudAdicional = currentChat.value.custom_attributes?.solicitud_adicional;

      if (ayudaAdicional === 'Si' && !solicitudAdicional) {
        useAlert(
          t(`No puedes resolver la conversación sin diligenciar el campo Solicitud adicional`)
        );
        return;
      }
    } else if (rawChat.inbox_id === 11) {

      const requiredFields = [
        {
          name: 'Tipificación',
          field: 'tipificacion_redverde'
        }
      ];

      const customAttributes = currentChat.value.custom_attributes || {};

      // buscar los que faltan o están vacíos
      const missingFields = requiredFields.filter(
        f => !customAttributes[f.field] || String(customAttributes[f.field]).trim() === ''
      );

      if (missingFields.length > 0) {
        const missingNames = missingFields.map(f => f.name).join(', ');
        useAlert(
          t(`No puedes resolver la conversación sin diligenciar los siguientes campos: ${missingNames}`)
        );
        return;
      }
    }
    toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
  }
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      onCmdResolveConversation();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation();

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div class="flex relative justify-end items-center resolve-actions">
    <ButtonGroup class="flex-shrink-0 rounded-lg shadow outline-1 outline"
      :class="!showOpenButton ? 'outline-n-container' : 'outline-transparent'">
      <Button v-if="isOpen" :label="t('CONVERSATION.HEADER.RESOLVE_ACTION')" size="sm" color="slate" no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0" :is-loading="isLoading"
        @click="onCmdResolveConversation" />
      <Button v-else-if="isResolved" :label="t('CONVERSATION.HEADER.REOPEN_ACTION')" size="sm" color="slate"
        no-animation class="ltr:rounded-r-none rtl:rounded-l-none !outline-0" :is-loading="isLoading"
        @click="onCmdOpenConversation" />
      <Button v-else-if="showOpenButton" :label="t('CONVERSATION.HEADER.OPEN_ACTION')" size="sm" color="slate"
        no-animation :is-loading="isLoading" @click="onCmdOpenConversation" />
      <Button v-if="showAdditionalActions" ref="arrowDownButtonRef" icon="i-lucide-chevron-down" :disabled="isLoading"
        size="sm" no-animation class="ltr:rounded-l-none rtl:rounded-r-none !outline-0" color="slate" trailing-icon
        @click="openDropdown" />
    </ButtonGroup>
    <div v-if="showActionsDropdown" v-on-clickaway="closeDropdown"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0">
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem v-if="!isPending">
          <Button :label="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')" ghost slate sm start
            icon="i-lucide-alarm-clock-minus" class="w-full" @click="() => openSnoozeModal()" />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isPending">
          <Button :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')" ghost slate sm start
            icon="i-lucide-circle-dot-dashed" class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.PENDING)" />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
    <ConversationResolveAttributesModal ref="resolveAttributesModalRef" @submit="handleResolveWithAttributes" />
  </div>
</template>
